import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Banknote, Printer, Loader2 } from "lucide-react";

const modes = [
  { key: "cash", label: "Cash" },
  { key: "upi", label: "PhonePe / GPay / UPI" },
  { key: "bank", label: "Bank Transfer" },
];

// Partial/full mediator settlement with live breakdown + print voucher.
export default function SettlementBox({ vehicle, hold, onSettled, onPrint, compact }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [busy, setBusy] = useState(false);

  const total = vehicle.total_buying_cost || 0;
  const paidPrev = vehicle.mediator_paid || 0;
  const entered = parseFloat(amount || 0) || 0;
  const today = Math.min(Math.max(entered, 0), hold);
  const remaining = Math.max(hold - today, 0);

  const buildVoucher = () => ({ vehicle, total, paidPrev, today, remaining, mode });

  const doSettleAndPrint = async () => {
    if (today <= 0) return toast.error("Enter an amount to settle");
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/settle-mediator`, { amount: today, mode });
      toast.success(`Settled ${formatINR(today)} to mediator`);
      // capture voucher from pre-reload values, then print + refresh
      onPrint && onPrint(buildVoucher());
      onSettled && onSettled();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-slate-50" data-testid="settlement-box">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="a1-label">Amount to Pay Today</label>
          <input data-testid="settle-amount" type="number" className="a1-input" value={amount}
            onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount to pay" />
        </div>
        <div>
          <label className="a1-label">Payment Mode</label>
          <select data-testid="settle-mode" className="a1-input" value={mode} onChange={(e) => setMode(e.target.value)}>
            {modes.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
      </div>

      {/* Real-time breakdown — clearing held car (purchase) balance */}
      <div className="rounded-lg bg-white border border-slate-200 p-3 mb-3 text-sm space-y-1.5" data-testid="settle-breakdown">
        <Line label="Total Purchase Rate" value={formatINR(total)} />
        <Line label="Upfront Paid Previously" value={formatINR(paidPrev)} />
        <Line label="Paid Today" value={formatINR(today)} color="#16A34A" strong />
        <div className="border-t border-slate-200 my-1" />
        <Line label="Remaining Held Balance" value={formatINR(remaining)} color={remaining > 0 ? "#EA580C" : "#16A34A"} strong />
      </div>

      <button data-testid="settle-print-btn" onClick={doSettleAndPrint} disabled={busy} className={`a1-btn a1-btn-action w-full ${compact ? "!h-11 !text-sm" : ""}`}>
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Banknote className="w-5 h-5" /> <Printer className="w-5 h-5" /> Settle Balance &amp; Print Slip</>}
      </button>
    </div>
  );
}

function Line({ label, value, color, strong }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500 font-semibold">{label}</span>
      <span className={`num ${strong ? "font-black" : "font-bold"}`} style={{ color: color || "#0F172A" }}>{value}</span>
    </div>
  );
}
